import {css} from 'styled-components';

import {ActionStyleTextComponent} from 'components/design-guide/Action/Hyperlink';
import {TextLinkState} from 'components/design-guide/Action/Hyperlink/types';
import {ActionProps} from 'components/design-guide/Action/types';
import {ActionStyle, TextLinkStyle} from 'components/design-guide/enums';
import {Colors} from 'styles/Colors';

export enum TextLinkColor {
  DEFAULT_DEFAULT = Colors.PINE_GREEN,
  DEFAULT_HOVER = Colors.PINE_GREEN,
  DEFAULT_FOCUS = Colors.PINE_GREEN,
  DEFAULT_ACTIVE = Colors.PINE_GREEN,
  DEFAULT_DISABLED = Colors.DISABLED_GREY,

  FOOTER_NAV_LINK_DEFAULT = Colors.BROWN_GREY,
  FOOTER_NAV_LINK_HOVER = Colors.NEAR_BLACK,
  FOOTER_NAV_LINK_FOCUS = Colors.NEAR_BLACK,
  FOOTER_NAV_LINK_ACTIVE = Colors.NEAR_BLACK,
  FOOTER_NAV_LINK_DISABLED = Colors.DISABLED_GREY,

  MOBILE_NAV_LINK_DEFAULT = Colors.NEAR_BLACK,
  MOBILE_NAV_LINK_HOVER = Colors.NEAR_BLACK,
  MOBILE_NAV_LINK_FOCUS = Colors.NEAR_BLACK,
  MOBILE_NAV_LINK_ACTIVE = Colors.NEAR_BLACK,
  MOBILE_NAV_LINK_DISABLED = Colors.DISABLED_GREY,

  WARNING_LINK_DEFAULT = Colors.WARNING_RED,
  WARNING_LINK_HOVER = Colors.WARNING_RED,
  WARNING_LINK_FOCUS = Colors.WARNING_RED,
  WARNING_LINK_ACTIVE = Colors.WARNING_RED,
  WARNING_LINK_DISABLED = Colors.WARNING_RED
}

export interface TextLinkStateStyles {
  color: TextLinkColor;
  underline: boolean;
}

const DEFAULT_COLORS = new Map<TextLinkState, TextLinkStateStyles>([
  [
    'default',
    {
      color: TextLinkColor.DEFAULT_DEFAULT,
      underline: true
    }
  ],
  [
    'hover',
    {
      color: TextLinkColor.DEFAULT_HOVER,
      underline: false
    }
  ],
  [
    'focus',
    {
      color: TextLinkColor.DEFAULT_FOCUS,
      underline: false
    }
  ],
  [
    'active',
    {
      color: TextLinkColor.DEFAULT_ACTIVE,
      underline: false
    }
  ],
  [
    'disabled',
    {
      color: TextLinkColor.DEFAULT_DISABLED,
      underline: false
    }
  ]
]);

const FOOTER_NAV_LINK_COLORS = new Map<TextLinkState, TextLinkStateStyles>([
  [
    'default',
    {
      color: TextLinkColor.FOOTER_NAV_LINK_DEFAULT,
      underline: true
    }
  ],
  [
    'hover',
    {
      color: TextLinkColor.FOOTER_NAV_LINK_HOVER,
      underline: false
    }
  ],
  [
    'focus',
    {
      color: TextLinkColor.FOOTER_NAV_LINK_FOCUS,
      underline: false
    }
  ],
  [
    'active',
    {
      color: TextLinkColor.FOOTER_NAV_LINK_ACTIVE,
      underline: false
    }
  ],
  [
    'disabled',
    {
      color: TextLinkColor.FOOTER_NAV_LINK_DISABLED,
      underline: false
    }
  ]
]);

const MOBILE_NAV_LINK_COLORS = new Map<TextLinkState, TextLinkStateStyles>([
  [
    'default',
    {
      color: TextLinkColor.MOBILE_NAV_LINK_DEFAULT,
      underline: true
    }
  ],
  [
    'hover',
    {
      color: TextLinkColor.MOBILE_NAV_LINK_HOVER,
      underline: false
    }
  ],
  [
    'focus',
    {
      color: TextLinkColor.MOBILE_NAV_LINK_FOCUS,
      underline: false
    }
  ],
  [
    'active',
    {
      color: TextLinkColor.MOBILE_NAV_LINK_ACTIVE,
      underline: false
    }
  ],
  [
    'disabled',
    {
      color: TextLinkColor.MOBILE_NAV_LINK_DISABLED,
      underline: false
    }
  ]
]);

const WARNING_LINK_COLORS = new Map<TextLinkState, TextLinkStateStyles>([
  [
    'default',
    {
      color: TextLinkColor.WARNING_LINK_DEFAULT,
      underline: true
    }
  ],
  [
    'hover',
    {
      color: TextLinkColor.WARNING_LINK_HOVER,
      underline: false
    }
  ],
  [
    'focus',
    {
      color: TextLinkColor.WARNING_LINK_FOCUS,
      underline: false
    }
  ],
  [
    'active',
    {
      color: TextLinkColor.WARNING_LINK_ACTIVE,
      underline: false
    }
  ],
  [
    'disabled',
    {
      color: TextLinkColor.WARNING_LINK_DISABLED,
      underline: false
    }
  ]
]);

const COLOR_MAP = new Map<TextLinkStyle, Map<TextLinkState, TextLinkStateStyles>>([
  [TextLinkStyle.DEFAULT, DEFAULT_COLORS],
  [TextLinkStyle.FOOTER_NAV_LINK, FOOTER_NAV_LINK_COLORS],
  [TextLinkStyle.WARNING_LINK, WARNING_LINK_COLORS],
  [TextLinkStyle.MOBILE_NAV_LINK, MOBILE_NAV_LINK_COLORS]
]);

const generateCSSSetForState = (styles?: TextLinkStateStyles, suppressUnderline?: boolean) => {
  if (!styles) {
    return css``;
  }

  return css`
    color: ${styles.color};
    text-decoration: ${styles.underline && !suppressUnderline ? 'underline' : 'none'};
  `;
};

export const generateTextLinkStylesCSS = (props: ActionProps) => {
  if (props.actionStyle !== ActionStyle.TEXT && props.actionStyle !== ActionStyle.INLINE_TEXT) {
    return css``;
  }

  const textLinkStyle = props.styleProps?.textLinkStyle ?? TextLinkStyle.DEFAULT;
  const suppressUnderline = props.styleProps?.suppressUnderline;

  const defaultColors = COLOR_MAP.get(textLinkStyle)?.get('default');
  const hoverColors = COLOR_MAP.get(textLinkStyle)?.get('hover');
  const focusColors = COLOR_MAP.get(textLinkStyle)?.get('focus');
  const activeColors = COLOR_MAP.get(textLinkStyle)?.get('active');
  const disabledColors = COLOR_MAP.get(textLinkStyle)?.get('disabled');

  return css`
    ${ActionStyleTextComponent} {
      ${generateCSSSetForState(defaultColors, suppressUnderline)};
    }

    &:focus > ${ActionStyleTextComponent} {
      ${generateCSSSetForState(focusColors, suppressUnderline)};
    }

    &:hover > ${ActionStyleTextComponent} {
      ${generateCSSSetForState(hoverColors, suppressUnderline)};
    }

    &:active > ${ActionStyleTextComponent} {
      ${generateCSSSetForState(activeColors, suppressUnderline)};
    }

    &:disabled > ${ActionStyleTextComponent} {
      ${generateCSSSetForState(disabledColors, suppressUnderline)};
      cursor: not-allowed;
    }
  `;
};
